import { ExtractComponentProp } from '@approvalmax/types';
import { RowActionMenuItem, SortOrder, Table } from '@approvalmax/ui/src/components';
import { hooks } from '@approvalmax/utils';
import { stringify } from 'query-string';
import { useCallback, useMemo } from 'react';
import { TransactionsSorting, useGetTransactions } from 'shared/data/wallets/v1';

import { FilterValues } from '../TransactionsFilter/TransactionsFilter.types';
import { normalizeFilterValues } from '../TransactionsTab/TransactionsTab.helpers';
import { defaultSortState, itemsPerPage } from './TransactionsList.constants';
import { parseSortValuesFromQuery } from './TransactionsList.helpers';
import { messages } from './TransactionsList.messages';
import { ColumnId, Transaction, TransactionSort } from './TransactionsList.types';

export const useTransactionMenu = () => {
    const menuItems = useMemo<RowActionMenuItem<Transaction>[]>(
        () => [
            {
                label: messages.transactionDetails,
                onClick: () => {
                    console.info('View transaction details');
                },
            },
            {
                label: messages.downloadPaymentConfirmation,
                onClick: () => {
                    console.info('Download payment confirmation');
                },
            },
        ],
        []
    );

    return menuItems;
};

export const useTransactionsSort = () => {
    const [sortQuery, setSortQuery] = hooks.useQueryParam('sort');
    const [, setPageQuery] = hooks.useQueryParam('page');

    const transactionsSort = useMemo(() => {
        return sortQuery ? parseSortValuesFromQuery(sortQuery) : defaultSortState;
    }, [sortQuery]);

    const handleSortChange = useCallback<ExtractComponentProp<typeof Table, 'onSort'>>(
        (columnId, newSortOrder) => {
            setSortQuery(
                stringify({
                    ...transactionsSort,
                    [columnId]: newSortOrder,
                })
            );
            setPageQuery('0');
        },
        [transactionsSort, setSortQuery, setPageQuery]
    );

    return {
        transactionsSort,
        handleSortChange,
    };
};

export const useTransactions = (options: {
    companyId: string;
    page: number;
    filter: FilterValues;
    sort: TransactionSort;
}) => {
    const { companyId, page, filter, sort } = options;

    const filterValue = useMemo(() => {
        return normalizeFilterValues(filter);
    }, [filter]);

    const sortQuery = useMemo(() => {
        const sortList: TransactionsSorting[] = [];

        if (sort[ColumnId.Amount]) {
            sortList.push(
                sort[ColumnId.Amount] === SortOrder.asc
                    ? TransactionsSorting.accountAmount
                    : TransactionsSorting.ValueMinusaccountAmount
            );
        }

        if (sort[ColumnId.CreatedDate]) {
            sortList.push(
                sort[ColumnId.CreatedDate] === SortOrder.asc
                    ? TransactionsSorting.createdAt
                    : TransactionsSorting.ValueMinuscreatedAt
            );
        }

        if (sort[ColumnId.Status]) {
            sortList.push(
                sort[ColumnId.Status] === SortOrder.asc
                    ? TransactionsSorting.status
                    : TransactionsSorting.ValueMinusstatus
            );
        }

        if (sort[ColumnId.Type]) {
            sortList.push(
                sort[ColumnId.Type] === SortOrder.asc ? TransactionsSorting.type : TransactionsSorting.ValueMinustype
            );
        }

        return sortList;
    }, [sort]);

    const { data: transactions, isFetching: isFetchingGetTransactions } = useGetTransactions({
        path: {
            organizationId: companyId,
        },
        query: {
            limit: itemsPerPage,
            offset: page * itemsPerPage,
            sort: sortQuery,
            ...filterValue,
        },
    });

    return {
        transactions: transactions?.items ?? [],
        hasMore: transactions?.hasMore ?? false,
        totalItems: transactions?.totalItems ?? 0,
        isLoading: isFetchingGetTransactions,
    };
};
